import React, { lazy } from 'react';
import { DropletIcon, FlameIcon, XCircleIcon, CheckCircleIcon, AlertCircleIcon, ThermometerIcon, BatteryChargingIcon, ArrowDownIcon, AlertTriangleIcon, ClockIcon, CheckIcon, XIcon, DollarSignIcon, HomeIcon, ShieldCheckIcon } from 'lucide-react';
export function Infographic() {
  return <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-325">
      {/* Header */}
      <div className="text-center mb-16" data-id="element-326">
        <div className="inline-block bg-teal-100 text-teal-800 px-4 py-1 rounded-full text-sm font-bold mb-4" data-id="element-327">
          SPECIAL REPORT
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-328">
          You Don't Have{' '}
          <span className="line-through text-red-500" data-id="element-329">Dry Eyes</span> — You Have
          an <span className="text-teal-600" data-id="element-330">Oil Emergency</span>
        </h1>
        <p className="text-xl text-gray-700 mb-6 max-w-3xl mx-auto" data-id="element-331">
          <span className="font-semibold" data-id="element-332">86% of patients</span> with dry eye
          symptoms are misdiagnosed and using treatments that make their
          condition worse.
        </p>
        <div className="flex items-center justify-center mb-8" data-id="element-333">
          <div className="w-16 h-16 rounded-full bg-gray-200 mr-4 overflow-hidden" data-id="element-334">
            <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-1.2.1&auto=format&fit=crop&w=128&q=80" alt="Dr. Jennifer Martinez" className="w-full h-full object-cover" width="64" height="64" loading="lazy" decoding="async" data-id="element-335" />
          </div>
          <div className="text-left" data-id="element-336">
            <p className="font-medium text-gray-900" data-id="element-337">Dr. Jennifer Martinez</p>
            <p className="text-sm text-gray-600" data-id="element-338">
              Board Certified Ophthalmologist
            </p>
          </div>
        </div>
      </div>
      {/* The Problem Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-16" data-id="element-339">
        <div className="bg-red-500 px-6 py-3" data-id="element-340">
          <h2 className="text-2xl font-bold text-white" data-id="element-341">THE PROBLEM</h2>
        </div>
        <div className="p-6" data-id="element-342">
          <div className="flex flex-col md:flex-row gap-8 items-center mb-8" data-id="element-343">
            <div className="md:w-1/3" data-id="element-344">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gAbKZ31vhiEjG5eiDkKcsR/Gemini_Generated_Image_phlh9mphlh9mphlh.webp" alt="Eye drop bottles in a trash bin with red X marks showing they're ineffective" className="rounded-lg w-full h-auto shadow-md" width="400" height="300" loading="lazy" decoding="async" data-id="element-345" />
            </div>
            <div className="md:w-2/3" data-id="element-346">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-347">
                The Endless Cycle of Eye Drops
              </h3>
              <div className="space-y-3" data-id="element-348">
                <div className="flex items-start" data-id="element-349">
                  <XCircleIcon className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-350" />
                  <p className="text-gray-700" data-id="element-351">
                    You use drops for temporary relief, but symptoms return
                    quickly
                  </p>
                </div>
                <div className="flex items-start" data-id="element-352">
                  <XCircleIcon className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-353" />
                  <p className="text-gray-700" data-id="element-354">
                    Drops wash away your natural oils, making the problem worse
                  </p>
                </div>
                <div className="flex items-start" data-id="element-355">
                  <XCircleIcon className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-356" />
                  <p className="text-gray-700" data-id="element-357">
                    Your eyes become dependent on artificial tears
                  </p>
                </div>
                <div className="flex items-start" data-id="element-358">
                  <XCircleIcon className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-359" />
                  <p className="text-gray-700" data-id="element-360">
                    The underlying condition worsens while symptoms are masked
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center my-6" data-id="element-361">
            <ArrowDownIcon className="w-12 h-12 text-teal-500" data-id="element-362" />
          </div>
          <div className="bg-blue-50 p-6 rounded-lg mb-8" data-id="element-363">
            <h3 className="text-xl font-bold text-center text-gray-900 mb-4" data-id="element-364">
              The Real Issue: Oil, Not Water
            </h3>
            <div className="flex flex-col md:flex-row gap-8 items-center" data-id="element-365">
              <div className="md:w-1/3" data-id="element-366">
                <div className="bg-white rounded-lg shadow p-4" data-id="element-367">
                  <h4 className="font-bold text-center mb-4" data-id="element-368">
                    Healthy Tear Structure
                  </h4>
                  <div className="space-y-4" data-id="element-369">
                    <div className="flex items-center" data-id="element-370">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3" data-id="element-371">
                        <DropletIcon className="w-5 h-5 text-blue-500" data-id="element-372" />
                      </div>
                      <div data-id="element-373">
                        <p className="font-medium" data-id="element-374">Water Layer</p>
                        <p className="text-sm text-gray-600" data-id="element-375">
                          Provides moisture
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-376">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mr-3" data-id="element-377">
                        <FlameIcon className="w-5 h-5 text-teal-500" data-id="element-378" />
                      </div>
                      <div data-id="element-379">
                        <p className="font-medium" data-id="element-380">Oil Layer</p>
                        <p className="text-sm text-gray-600" data-id="element-381">
                          Prevents evaporation
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:w-2/3" data-id="element-382">
                <div className="space-y-3" data-id="element-383">
                  <div className="flex items-start" data-id="element-384">
                    <CheckCircleIcon className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-385" />
                    <p className="text-gray-700" data-id="element-386">
                      <span className="font-bold" data-id="element-387">86% of "dry eye" cases</span>{' '}
                      are actually oil gland dysfunction
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-388">
                    <CheckCircleIcon className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-389" />
                    <p className="text-gray-700" data-id="element-390">
                      Your tears evaporate too quickly because they lack proper
                      oil
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-391">
                    <CheckCircleIcon className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-392" />
                    <p className="text-gray-700" data-id="element-393">
                      Adding more water (drops) doesn't fix an oil problem
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-394">
                    <CheckCircleIcon className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-395" />
                    <p className="text-gray-700" data-id="element-396">
                      Without treatment, oil glands can be permanently damaged
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center my-6" data-id="element-397">
            <ArrowDownIcon className="w-12 h-12 text-teal-500" data-id="element-398" />
          </div>
          <div data-id="element-399">
            <h3 className="text-xl font-bold text-center text-gray-900 mb-4" data-id="element-400">
              What's Clogging Your Oil Glands?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-401">
              <div className="flex items-start" data-id="element-402">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-403">
                  <AlertCircleIcon className="w-5 h-5 text-red-500" data-id="element-404" />
                </div>
                <div data-id="element-405">
                  <p className="font-medium text-gray-900" data-id="element-406">
                    Digital Device Use
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-407">
                    Screen time reduces blink rate by 60%
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-408">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-409">
                  <ThermometerIcon className="w-5 h-5 text-red-500" data-id="element-410" />
                </div>
                <div data-id="element-411">
                  <p className="font-medium text-gray-900" data-id="element-412">
                    Indoor Environment
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-413">
                    AC, heating, and low humidity
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-414">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-415">
                  <DropletIcon className="w-5 h-5 text-red-500" data-id="element-416" />
                </div>
                <div data-id="element-417">
                  <p className="font-medium text-gray-900" data-id="element-418">Aging</p>
                  <p className="text-sm text-gray-700" data-id="element-419">
                    Natural decrease in oil production
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-420">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-421">
                  <BatteryChargingIcon className="w-5 h-5 text-red-500" data-id="element-422" />
                </div>
                <div data-id="element-423">
                  <p className="font-medium text-gray-900" data-id="element-424">Hormonal Changes</p>
                  <p className="text-sm text-gray-700" data-id="element-425">
                    Affects oil quality and quantity
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Warning Section */}
      <div className="bg-red-50 rounded-xl border-2 border-red-200 p-6 mb-16 relative" data-id="element-426">
        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-white p-2 rounded-full border-2 border-red-200" data-id="element-427">
          <AlertTriangleIcon className="w-10 h-10 text-red-500" data-id="element-428" />
        </div>
        <h2 className="text-2xl font-bold text-center text-gray-900 mt-6 mb-6" data-id="element-429">
          The Dangers of Doing Nothing
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-430">
          <div className="bg-white p-4 rounded-lg shadow text-center" data-id="element-431">
            <h3 className="font-bold text-gray-900 mb-2" data-id="element-432">Early Stage</h3>
            <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-433">
              <span className="text-2xl font-bold text-yellow-700" data-id="element-434">90%</span>
            </div>
            <p className="text-sm" data-id="element-435">Glands recoverable with proper treatment</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center" data-id="element-436">
            <h3 className="font-bold text-gray-900 mb-2" data-id="element-437">Late Stage</h3>
            <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-orange-100 flex items-center justify-center" data-id="element-438">
              <span className="text-2xl font-bold text-orange-700" data-id="element-439">50%</span>
            </div>
            <p className="text-sm" data-id="element-440">Glands may be salvageable</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center" data-id="element-441">
            <h3 className="font-bold text-gray-900 mb-2" data-id="element-442">End Stage</h3>
            <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-red-100 flex items-center justify-center" data-id="element-443">
              <span className="text-2xl font-bold text-red-700" data-id="element-444">0%</span>
            </div>
            <p className="text-sm" data-id="element-445">Complete gland loss - irreversible</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg" data-id="element-446">
          <p className="text-center text-gray-800 font-medium" data-id="element-447">
            <span className="text-red-600 font-bold" data-id="element-448">Warning:</span> The average
            person loses up to 30% of their functioning oil glands by age 50 — a
            process that accelerates with untreated dysfunction.
          </p>
        </div>
      </div>
      {/* Solution Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-16" data-id="element-449">
        <div className="bg-teal-500 px-6 py-3" data-id="element-450">
          <h2 className="text-2xl font-bold text-white" data-id="element-451">THE SOLUTION</h2>
        </div>
        <div className="p-6" data-id="element-452">
          <div className="bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg overflow-hidden shadow-lg mb-10" data-id="element-453">
            <div className="flex flex-col md:flex-row" data-id="element-454">
              <div className="md:w-1/2 relative" data-id="element-455">
                <div className="w-full aspect-[4/3] overflow-hidden" data-id="element-456">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oqbCrMuCmYbTzWxaKpnJ18/Gemini_Generated_Image_esm7r8esm7r8esm7_%281%29.webp" alt="Person relaxing in bed while wearing the Oculis 3 eye therapy device" className="w-full h-full object-cover object-top" width="500" height="375" loading="lazy" decoding="async" style={{
                  objectPosition: 'center 40%'
                }} data-id="element-457" />
                </div>
              </div>
              <div className="md:w-1/2 p-6 flex flex-col justify-center" data-id="element-458">
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-459">
                  Introducing Oculis 3
                </h3>
                <p className="text-white mb-4" data-id="element-460">
                  The at-home heat therapy device that melts away clogged oils
                  and restores healthy tear film
                </p>
                <a href="https://irelief.us/products/vibration-bluetooth-eye-massager-visual-eye-massage-intelligent-eye-mask-massage-masajeador-heating-eyemask-face-facial-massager" data-id="element-461">
                  <button className="bg-white text-teal-600 hover:bg-gray-100 transition-colors px-6 py-3 rounded-full font-bold shadow-md" data-id="element-462">
                    Order Now
                  </button>
                </a>
              </div>
            </div>
          </div>
          <h3 className="text-xl font-bold text-center text-gray-900 mb-6" data-id="element-463">
            How It Works: The 12-Minute Daily Ritual
          </h3>
          <div className="flex flex-col md:flex-row gap-8 mb-10" data-id="element-464">
            <div className="md:w-1/2" data-id="element-465">
              <div className="flex items-center mb-4" data-id="element-466">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mr-3" data-id="element-467">
                  <ClockIcon className="w-5 h-5 text-teal-500" data-id="element-468" />
                </div>
                <p className="font-medium text-gray-900" data-id="element-469">
                  Just 12 minutes daily
                </p>
              </div>
              <ol className="space-y-4 mb-6" data-id="element-470">
                <li className="flex" data-id="element-471">
                  <div className="bg-teal-100 w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-472">
                    <span className="font-bold text-teal-800" data-id="element-473">1</span>
                  </div>
                  <div data-id="element-474">
                    <p className="font-medium text-gray-800" data-id="element-475">
                      Therapeutic Heat
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-476">
                      Precise 103°F temperature melts hardened oils
                    </p>
                  </div>
                </li>
                <li className="flex" data-id="element-477">
                  <div className="bg-teal-100 w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-478">
                    <span className="font-bold text-teal-800" data-id="element-479">2</span>
                  </div>
                  <div data-id="element-480">
                    <p className="font-medium text-gray-800" data-id="element-481">Gentle Pressure</p>
                    <p className="text-sm text-gray-600" data-id="element-482">
                      Helps express melted oils naturally
                    </p>
                  </div>
                </li>
                <li className="flex" data-id="element-483">
                  <div className="bg-teal-100 w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-484">
                    <span className="font-bold text-teal-800" data-id="element-485">3</span>
                  </div>
                  <div data-id="element-486">
                    <p className="font-medium text-gray-800" data-id="element-487">
                      Daily Maintenance
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-488">
                      Prevents re-clogging and restores function
                    </p>
                  </div>
                </li>
              </ol>
            </div>
            <div className="md:w-1/2" data-id="element-489">
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-100 mb-4" data-id="element-490">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-491">
                  Clinical Results
                </h4>
                <div className="space-y-2" data-id="element-492">
                  <div className="flex items-start" data-id="element-493">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-494" />
                    <p className="text-sm text-gray-700" data-id="element-495">
                      86% reduction in symptoms after 4 weeks
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-496">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-497" />
                    <p className="text-sm text-gray-700" data-id="element-498">
                      77% decrease in artificial tear usage
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-499">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-500" />
                    <p className="text-sm text-gray-700" data-id="element-501">
                      90% improved comfort with contact lenses
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-502">
                <div className="flex" data-id="element-503">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vGhF6X5ZCzNmfeLx3aqMmt/imgi_175_7cc21bd70030c675d629ad8551895491.webp" alt="Eye condition improvement over three weeks" className="w-full h-auto rounded-lg" width="400" height="200" loading="lazy" decoding="async" data-id="element-504" />
                </div>
                <p className="text-xs text-gray-600 mt-2 text-center italic" data-id="element-505">
                  Actual patient results showing improvement over three weeks
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg mb-10" data-id="element-506">
            <h3 className="text-xl font-bold text-center text-gray-900 mb-4" data-id="element-507">
              Cost Comparison
            </h3>
            <div className="flex flex-col md:flex-row gap-6" data-id="element-508">
              <div className="md:w-1/2 bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-509">
                <div className="flex items-center mb-3" data-id="element-510">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3" data-id="element-511">
                    <DollarSignIcon className="w-5 h-5 text-red-500" data-id="element-512" />
                  </div>
                  <h4 className="font-bold text-gray-900" data-id="element-513">
                    In-Office Treatment
                  </h4>
                </div>
                <ul className="space-y-2 text-sm" data-id="element-514">
                  <li className="flex items-start" data-id="element-515">
                    <XIcon className="w-4 h-4 text-red-500 mr-2 mt-0.5" data-id="element-516" />
                    <p className="text-gray-700" data-id="element-517">
                      <span className="font-medium" data-id="element-518">$800-$12300</span> per
                      session
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-519">
                    <XIcon className="w-4 h-4 text-red-500 mr-2 mt-0.5" data-id="element-520" />
                    <p className="text-gray-700" data-id="element-521">
                      Multiple office visits required
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-522">
                    <XIcon className="w-4 h-4 text-red-500 mr-2 mt-0.5" data-id="element-523" />
                    <p className="text-gray-700" data-id="element-524">
                      Annual cost:{' '}
                      <span className="font-medium" data-id="element-525">$1,600-$3,200</span>
                    </p>
                  </li>
                </ul>
                <div className="mt-4" data-id="element-526">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/taJK9EfqAB5f35Z8npD9LA/unnamed_%281%29.png" alt="Medical professional in iRelief lab coat with Oculis 3 device and eye anatomy diagram on screen" className="w-full h-auto rounded-lg shadow-sm" width="300" height="200" loading="lazy" decoding="async" style={{
                  objectFit: 'cover',
                  aspectRatio: '3/2'
                }} data-id="element-527" />
                </div>
              </div>
              <div className="md:w-1/2 bg-white p-4 rounded-lg border border-teal-200 shadow-sm" data-id="element-528">
                <div className="flex items-center mb-3" data-id="element-529">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mr-3" data-id="element-530">
                    <HomeIcon className="w-5 h-5 text-teal-500" data-id="element-531" />
                  </div>
                  <h4 className="font-bold text-gray-900" data-id="element-532">Oculis 3 At-Home</h4>
                </div>
                <ul className="space-y-2 text-sm" data-id="element-533">
                  <li className="flex items-start" data-id="element-534">
                    <CheckIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5" data-id="element-535" />
                    <p className="text-gray-700" data-id="element-536">
                      <span className="font-medium" data-id="element-537">One-time</span> purchase of
                      $79.99-119.99
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-538">
                    <CheckIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5" data-id="element-539" />
                    <p className="text-gray-700" data-id="element-540">
                      Use daily in the comfort of home
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-541">
                    <CheckIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5" data-id="element-542" />
                    <p className="text-gray-700" data-id="element-543">
                      Annual cost after purchase:{' '}
                      <span className="font-medium" data-id="element-544">$0</span>
                    </p>
                  </li>
                </ul>
                <div className="mt-4" data-id="element-545">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3xZKDbSqqoqNpX1A998VFr/Gemini_Generated_Image_xgmywwxgmywwxgmy.jpg" alt="Person relaxing on a couch while using the Oculis 3 eye therapy device" className="w-full h-auto rounded-lg shadow-sm" width="300" height="200" loading="lazy" decoding="async" style={{
                  objectFit: 'cover',
                  aspectRatio: '3/2'
                }} data-id="element-546" />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 mb-6" data-id="element-547">
            <div className="text-4xl text-blue-200 absolute -ml-2 -mt-4" data-id="element-548">"</div>
            <p className="text-lg text-gray-800 mb-4 pl-6" data-id="element-549">
              I spent hundreds on eye drops with little relief. After two weeks
              of heat therapy, I was using drops once a day instead of hourly.
              After a month, I barely needed them at all.
            </p>
            <div className="flex items-center pl-6" data-id="element-550">
              <div className="w-10 h-10 rounded-full bg-gray-300 mr-3 overflow-hidden" data-id="element-551">
                <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=80&q=80" alt="Sarah J." className="w-full h-full object-cover" width="40" height="40" loading="lazy" decoding="async" data-id="element-552" />
              </div>
              <div data-id="element-553">
                <p className="font-medium text-gray-900" data-id="element-554">Sarah J.</p>
                <p className="text-xs text-gray-600" data-id="element-555">Marketing Executive, 42</p>
              </div>
            </div>
          </div>
          <div className="text-center" data-id="element-556">
            <a href="https://story.irelief.us" data-id="element-557">
              <button className="bg-gradient-to-r from-blue-500 to-teal-500 text-white hover:from-blue-600 hover:to-teal-600 transition-colors px-8 py-4 rounded-full font-bold text-lg shadow-lg" data-id="element-558">
                Still Not Convinced? Learn More
              </button>
            </a>
            <div className="text-sm mt-4 text-gray-600" data-id="element-559">
              Want to learn more about dry eyes and see what solution is best
              for you? Click the button above!
            </div>
          </div>
        </div>
      </div>
    </div>;
}